import type { FC, PropsWithChildren } from 'react'
import { createContext, useContext, useMemo } from 'react'

import { useChatChannelConfig } from '@liveHelper/context/ChatChannelConfigContext'
import { paramsSelector, useStore } from '@liveHelper/models/store'
import { useAppointmentQuery } from '@oneToOne/shared/hooks/useAppointmentQuery'
import type { Appointment } from '@oneToOne/shared/services/rest/types'

import { getGuestId } from '@core/tracking/guestId'

export const AppointmentContext = createContext<{
  appointment: Appointment | undefined
  refetchAppointment?: () => void
}>({ appointment: undefined })

/** @returns The appointment in the context. */
export function useAppointment() {
  return useContext(AppointmentContext)
}

/** Provides appointment context. */
export const AppointmentContextProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const chatChannel = useChatChannelConfig()
  const { api_host } = useStore(paramsSelector)
  const url = new URL(window.location.href)
  const appointmentId = url.searchParams.get('fw_appointment') || undefined
  const visitorId = getGuestId()
  const {
    data: appointment,
    isLoading,
    refetch,
  } = useAppointmentQuery(chatChannel?.id, appointmentId, visitorId, api_host)

  const appointmentContext = useMemo(
    () => ({
      appointment,
      refetchAppointment: refetch,
    }),
    [appointment, refetch],
  )

  if (appointmentId && isLoading) {
    return null
  }

  return (
    <AppointmentContext.Provider value={appointmentContext}>
      {children}
    </AppointmentContext.Provider>
  )
}
